/**
 * SurveyRenderer dinámico — Sprint 11 redesign.
 *
 * Renderiza un schema validado + responses + onChange. Cada pregunta
 * va dentro de un QuestionCard con indicador de estado visual
 * (answered/required-pending/pending/error) y badge de tipo.
 *
 * Sección activa puede ser controlada externamente (AssessmentCapture
 * con su sidebar) o internamente (Capture portal cliente sin sidebar).
 * Si parent pasa `activeSectionIndex` + `onSectionChange`, usa esos.
 * Si pasa `hideSectionNav`, no renderiza la barra de tabs de sección.
 *
 * Photo upload se delega via prop `onUploadPhoto`.
 */
import { type ChangeEvent, useMemo, useRef, useState } from 'react';
import { Camera } from 'lucide-react';

import type { MaturityLevelDef, Question, QuestionType, SurveyTemplate } from '@assessos/shared';
import {
  CMMI_LEVELS,
  NIST_CSF_TIERS,
  isAnswered,
  sectionProgress,
  questionState,
  progressPercent,
} from '@assessos/shared';

import { ProgressBar, QuestionCard, type QuestionStatus } from './ui.js';
import { Button } from './ui/Button.js';
import { EmptyState } from './ui/EmptyState.js';
import { LoadingSkeleton } from './ui/LoadingSkeleton.js';

interface UploadedPhotoMeta {
  id: string;
  file_url: string;
  size_bytes: number;
}

export interface QuestionPhotoSummary {
  id: string;
  file_url: string;
  /**
   * S20-B · Chips de normas adicionales para las que esta foto sirve
   * como evidencia (cross-tag IMS). Mismo campo que `photos.applicable_standards`
   * del backend (S16-B). Opcional para preservar retro-compat.
   */
  applicable_standards?: string[];
}

/** S20-B · Callback opcional para actualizar tagging IMS de una foto via PATCH. */
export type PatchPhotoStandardsHandler = (photoId: string, standards: string[]) => Promise<void>;

export interface SurveyRendererProps {
  schema: SurveyTemplate;
  responses: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  onUploadPhoto?: (questionRef: string, file: File) => Promise<UploadedPhotoMeta>;
  readOnly?: boolean;
  /** Si parent pasa este index, controla la sección activa. */
  activeSectionIndex?: number;
  /** Si parent pasa esto, recibe el cambio cuando se navega. */
  onSectionChange?: (idx: number) => void;
  /** Si true, oculta la barra de tabs de sección (parent la renderiza). */
  hideSectionNav?: boolean;
  /**
   * S15-C · Notas del auditor por pregunta. Si parent las pasa, se
   * activa el panel "Evidencia y notas" bajo cada QuestionCard.
   */
  auditorNotes?: Record<string, string>;
  onAuditorNoteChange?: (questionId: string, note: string) => void;
  /**
   * S15-C · Fotos ya subidas indexadas por questionRef. Render como
   * thumbnails dentro del panel evidencia. Si no se pasa, no se muestra
   * la galería (pero el botón "+ Foto" sigue disponible si onUploadPhoto está).
   */
  photosByQuestion?: Record<string, QuestionPhotoSummary[]>;
  /**
   * S20-B · Callback PATCH /api/v1/photos/:id para editar applicable_standards
   * desde la UI de evidencia. Si no se pasa, las chips se muestran read-only.
   */
  onPatchPhotoStandards?: PatchPhotoStandardsHandler;
  /**
   * ADR-040 D5 · Refs de preguntas pre-llenadas desde el insumo del cliente.
   * Cuando un questionId está en esta lista, se muestra un badge discreto
   * "dato del cliente" junto al campo para que el auditor lo confirme/ajuste.
   * Solo aplica en la instancia AUDITOR; no se pasa en la instancia CLIENTE.
   */
  prefillRefs?: string[];
}

const TYPE_LABEL: Record<QuestionType, string> = {
  text: 'TEXTO',
  textarea: 'NOTA',
  number: 'NÚMERO',
  single_select: 'SELECT',
  multi_select: 'MULTI-SELECT',
  boolean: 'SÍ / NO',
  date: 'FECHA',
  score_1_5: 'ESCALA 1–5',
  maturity_scale: 'MADUREZ',
  photo: 'EVIDENCIA FOTO',
  signature: 'FIRMA',
};

export function SurveyRenderer({
  schema,
  responses,
  onChange,
  onUploadPhoto,
  readOnly = false,
  activeSectionIndex,
  onSectionChange,
  hideSectionNav = false,
  auditorNotes,
  onAuditorNoteChange,
  photosByQuestion,
  onPatchPhotoStandards,
  prefillRefs,
}: SurveyRendererProps): JSX.Element {
  // ADR-040 D5: Set de refs pre-llenados para lookup O(1) en el render de cada pregunta.
  const prefillRefSet = prefillRefs && prefillRefs.length > 0 ? new Set(prefillRefs) : null;
  const [internalSec, setInternalSec] = useState(0);
  const activeSec = activeSectionIndex ?? internalSec;
  const sec = schema.sections[activeSec]!;
  const progress = useMemo(() => sectionProgress(sec, responses), [sec, responses]);

  function changeSection(idx: number): void {
    if (onSectionChange) onSectionChange(idx);
    else setInternalSec(idx);
  }

  function setResponse(qid: string, value: unknown): void {
    onChange({ ...responses, [qid]: value });
  }

  return (
    <div className="space-y-5">
      {!hideSectionNav && (
        <nav
          aria-label="Secciones"
          className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2"
        >
          {schema.sections.map((s, idx) => {
            const p = sectionProgress(s, responses);
            const pct = progressPercent(p);
            const active = idx === activeSec;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => changeSection(idx)}
                className={
                  'rounded-xl px-3 py-2 text-xs font-semibold transition-colors ' +
                  (active
                    ? 'bg-brand-700 text-white'
                    : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200')
                }
              >
                <span className="block text-left">
                  {String(idx + 1).padStart(2, '0')} · {s.title}
                </span>
                <span
                  className={`mt-0.5 block text-[10px] font-normal ${active ? 'text-white/80' : 'text-slate-400'}`}
                >
                  {p.answered}/{p.total} · {pct}%
                </span>
              </button>
            );
          })}
        </nav>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6">
        <header className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-700">
                Sección {activeSec + 1} de {schema.sections.length}
              </p>
              <h2 className="mt-1 text-xl font-semibold text-brand-900 tracking-tight">
                {sec.title}
              </h2>
              {sec.description && (
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">{sec.description}</p>
              )}
            </div>
            <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
              {progress.answered} / {progress.total} respondidas
            </span>
          </div>
          <ProgressBar percent={progressPercent(progress)} />
        </header>

        <div className="space-y-4">
          {sec.questions.map((q, idx) => {
            const value = responses[q.id];
            const error = validateField(q, value);
            const baseStatus: QuestionStatus = error ? 'error' : questionState(q, value);
            const evidenceEnabled =
              !!onAuditorNoteChange && auditorNotes !== undefined && q.type !== 'signature';
            // ADR-040 D5: badge de procedencia de prefill para el auditor.
            const isFromClient = prefillRefSet?.has(q.id) ?? false;
            return (
              <QuestionCard
                key={q.id}
                index={idx + 1}
                total={sec.questions.length}
                label={q.label}
                description={q.description}
                typeBadge={TYPE_LABEL[q.type]}
                required={q.required}
                status={baseStatus}
                controls={q.controls}
                sources={q.sources}
                prefillBadge={isFromClient}
              >
                {renderInput({
                  question: q,
                  value,
                  onChange: (v) => setResponse(q.id, v),
                  ...(onUploadPhoto ? { onUploadPhoto } : {}),
                  readOnly,
                })}
                {error && (
                  <p role="alert" className="mt-2 text-xs font-medium text-rose-600">
                    {error}
                  </p>
                )}
                {evidenceEnabled && (
                  <QuestionEvidence
                    questionId={q.id}
                    note={auditorNotes[q.id] ?? ''}
                    onNoteChange={(v) => onAuditorNoteChange(q.id, v)}
                    photos={photosByQuestion?.[q.id] ?? []}
                    {...(onUploadPhoto
                      ? { onUploadPhoto: (file: File) => onUploadPhoto(q.id, file) }
                      : {})}
                    {...(onPatchPhotoStandards ? { onPatchPhotoStandards } : {})}
                    readOnly={readOnly}
                  />
                )}
              </QuestionCard>
            );
          })}
        </div>

        <div className="flex justify-between gap-2 border-t border-slate-100 pt-5">
          <button
            type="button"
            disabled={activeSec === 0}
            onClick={() => changeSection(Math.max(0, activeSec - 1))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Sección anterior
          </button>
          <button
            type="button"
            disabled={activeSec === schema.sections.length - 1}
            onClick={() => changeSection(Math.min(schema.sections.length - 1, activeSec + 1))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Sección siguiente →
          </button>
        </div>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Field router
// ─────────────────────────────────────────────────────────────────────

interface FieldProps {
  question: Question;
  value: unknown;
  onChange: (v: unknown) => void;
  onUploadPhoto?: (questionRef: string, file: File) => Promise<UploadedPhotoMeta>;
  readOnly: boolean;
}

function renderInput(p: FieldProps): JSX.Element {
  switch (p.question.type) {
    case 'text':
      return <TextInput {...p} multiline={false} />;
    case 'textarea':
      return <TextInput {...p} multiline />;
    case 'number':
      return <NumberInput {...p} />;
    case 'single_select':
      return <SingleSelect {...p} />;
    case 'multi_select':
      return <MultiSelect {...p} />;
    case 'boolean':
      return <BooleanInput {...p} />;
    case 'date':
      return <DateInput {...p} />;
    case 'score_1_5':
      return <Score1to5 {...p} />;
    case 'maturity_scale':
      return <MaturityScale {...p} />;
    case 'photo':
      return <PhotoInput {...p} />;
    case 'signature':
      return <SignatureInput {...p} />;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Validation
// ─────────────────────────────────────────────────────────────────────

function validateField(q: Question, value: unknown): string | null {
  if (q.required && !isAnswered(value)) {
    return 'Campo obligatorio';
  }
  if (value === undefined || value === null || value === '') return null;
  switch (q.type) {
    case 'text':
    case 'textarea': {
      if (typeof value !== 'string') return 'Valor inválido';
      const v = q.validation;
      if (v?.minLength !== undefined && value.length < v.minLength)
        return `Mínimo ${String(v.minLength)} caracteres`;
      if (v?.maxLength !== undefined && value.length > v.maxLength)
        return `Máximo ${String(v.maxLength)} caracteres`;
      if (v?.regex !== undefined) {
        try {
          const rx = new RegExp(v.regex);
          if (!rx.test(value)) return 'Formato inválido';
        } catch {
          return null;
        }
      }
      return null;
    }
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(n)) return 'Número inválido';
      const v = q.validation;
      if (v?.min !== undefined && n < v.min) return `Mínimo ${String(v.min)}`;
      if (v?.max !== undefined && n > v.max) return `Máximo ${String(v.max)}`;
      if (v?.integer && !Number.isInteger(n)) return 'Debe ser entero';
      return null;
    }
    case 'multi_select': {
      if (!Array.isArray(value)) return 'Valor inválido';
      if (q.minSelections !== undefined && value.length < q.minSelections)
        return `Selecciona al menos ${String(q.minSelections)}`;
      if (q.maxSelections !== undefined && value.length > q.maxSelections)
        return `Selecciona máximo ${String(q.maxSelections)}`;
      return null;
    }
    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Field components — preservados de la versión previa con polish menor
// ─────────────────────────────────────────────────────────────────────

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-100';

function TextInput({
  question,
  value,
  onChange,
  multiline,
  readOnly,
}: FieldProps & { multiline: boolean }): JSX.Element {
  // aria-label asocia el control a la pregunta — WCAG 1.3.1 / axe rule "label"
  const props = {
    value: typeof value === 'string' ? value : '',
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(e.target.value),
    disabled: readOnly,
    className: inputClass,
    'aria-label': question.label,
  };
  return multiline ? <textarea rows={3} {...props} /> : <input type="text" {...props} />;
}

function NumberInput({ question, value, onChange, readOnly }: FieldProps): JSX.Element {
  const v = typeof value === 'number' ? String(value) : typeof value === 'string' ? value : '';
  const unit = question.type === 'number' ? question.unit : undefined;
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        inputMode="decimal"
        value={v}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') onChange(null);
          else {
            const n = Number(raw);
            onChange(Number.isNaN(n) ? raw : n);
          }
        }}
        disabled={readOnly}
        className={inputClass}
        aria-label={question.label}
      />
      {unit && <span className="text-xs font-medium text-slate-500">{unit}</span>}
    </div>
  );
}

function optionEntries(q: Extract<Question, { type: 'single_select' | 'multi_select' }>) {
  return q.options.map((o) => {
    if (typeof o === 'string' || typeof o === 'number') {
      return { value: o, label: String(o) };
    }
    return { value: o.value, label: o.label };
  });
}

function SingleSelect({ question, value, onChange, readOnly }: FieldProps): JSX.Element {
  if (question.type !== 'single_select') throw new Error('bad type');
  const opts = optionEntries(question);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {opts.map((o) => {
        const currentStr =
          typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
            ? String(value)
            : '';
        const selected = String(o.value) === currentStr;
        return (
          <button
            key={String(o.value)}
            type="button"
            disabled={readOnly}
            onClick={() => onChange(o.value)}
            className={
              'rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors ' +
              (selected
                ? 'border-brand-700 bg-brand-50 text-brand-900'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50')
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function MultiSelect({ question, value, onChange, readOnly }: FieldProps): JSX.Element {
  if (question.type !== 'multi_select') throw new Error('bad type');
  const opts = optionEntries(question);
  const selected = new Set(Array.isArray(value) ? value.map((v) => String(v)) : []);
  function toggle(v: string | number): void {
    const key = String(v);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(opts.filter((o) => next.has(String(o.value))).map((o) => o.value));
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {opts.map((o) => {
        const isSelected = selected.has(String(o.value));
        return (
          <label
            key={String(o.value)}
            className={
              'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors ' +
              (isSelected
                ? 'border-brand-700 bg-brand-50 text-brand-900'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50')
            }
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggle(o.value)}
              disabled={readOnly}
              className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-500/30"
            />
            <span className="font-medium">{o.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function BooleanInput({ question, value, onChange, readOnly }: FieldProps): JSX.Element {
  if (question.type !== 'boolean') throw new Error('bad type');
  const buttons: { v: boolean | null; label: string }[] = [
    { v: true, label: 'Sí' },
    { v: false, label: 'No' },
  ];
  if (question.allowNotApplicable) buttons.push({ v: null, label: 'N/A' });
  return (
    <div className="flex gap-2">
      {buttons.map((b) => {
        const selected = value === b.v;
        const toneClass =
          b.v === true
            ? selected
              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
              : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300'
            : b.v === false
              ? selected
                ? 'border-rose-500 bg-rose-50 text-rose-700'
                : 'border-slate-200 bg-white text-slate-700 hover:border-rose-300'
              : selected
                ? 'border-slate-500 bg-slate-100 text-slate-700'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300';
        return (
          <button
            key={String(b.v)}
            type="button"
            disabled={readOnly}
            onClick={() => onChange(b.v)}
            className={`flex-1 rounded-lg border-2 px-4 py-2.5 text-sm font-semibold transition-colors ${toneClass}`}
          >
            {b.label}
          </button>
        );
      })}
    </div>
  );
}

function DateInput({ question, value, onChange, readOnly }: FieldProps): JSX.Element {
  return (
    <input
      type="date"
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={readOnly}
      className={inputClass}
      aria-label={question.label}
    />
  );
}

function Score1to5({ question, value, onChange, readOnly }: FieldProps): JSX.Element {
  if (question.type !== 'score_1_5') throw new Error('bad type');
  const v = typeof value === 'number' ? value : null;
  const scaleLabels = question.scaleLabels;
  function labelFor(n: number): string | undefined {
    if (!scaleLabels) return undefined;
    const key = String(n) as keyof typeof scaleLabels;
    return scaleLabels[key];
  }
  return (
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map((n) => {
        const selected = v === n;
        const lbl = labelFor(n);
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onClick={() => onChange(n)}
            className={
              'flex-1 rounded-lg border-2 py-3 text-center font-semibold transition-colors ' +
              (selected
                ? 'border-brand-700 bg-brand-50 text-brand-900'
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700')
            }
          >
            <span className="block text-lg">{n}</span>
            {lbl && (
              <span
                className={`mt-0.5 block text-[10px] font-normal ${selected ? 'text-brand-700' : 'text-slate-400'}`}
              >
                {lbl}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function MaturityScale({ question, value, onChange, readOnly }: FieldProps): JSX.Element {
  if (question.type !== 'maturity_scale') throw new Error('bad type');
  const v = typeof value === 'number' ? value : null;
  const levels: readonly MaturityLevelDef[] =
    question.levels ?? (question.maturityModel === 'NIST_CSF_TIERS' ? NIST_CSF_TIERS : CMMI_LEVELS);
  const target = question.targetLevel ?? null;
  return (
    <div className="space-y-2">
      {levels.map((lvl) => {
        const selected = v === lvl.value;
        const isTarget = target === lvl.value;
        return (
          <button
            key={lvl.value}
            type="button"
            disabled={readOnly}
            onClick={() => onChange(lvl.value)}
            className={
              'flex w-full items-start gap-3 rounded-lg border-2 p-3 text-left transition-colors ' +
              (selected
                ? 'border-brand-700 bg-brand-50'
                : 'border-slate-200 bg-white hover:border-slate-300')
            }
          >
            <span
              className={
                'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ' +
                (selected ? 'bg-brand-700 text-white' : 'bg-slate-100 text-slate-500')
              }
            >
              {lvl.value}
            </span>
            <span className="flex-1">
              <span className="flex items-center gap-2">
                <span className={`font-semibold ${selected ? 'text-brand-900' : 'text-slate-700'}`}>
                  {lvl.label}
                </span>
                {isTarget && (
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                    Objetivo
                  </span>
                )}
              </span>
              <span
                className={`mt-1 block text-xs ${selected ? 'text-brand-700' : 'text-slate-500'}`}
              >
                {lvl.descriptor}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PhotoInput({
  question,
  value,
  onChange,
  onUploadPhoto,
  readOnly,
}: FieldProps): JSX.Element {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const photos = Array.isArray(value) ? (value as UploadedPhotoMeta[]) : [];

  async function handleFile(file: File): Promise<void> {
    if (!onUploadPhoto) {
      setErr('Upload no disponible');
      return;
    }
    setUploading(true);
    setErr(null);
    try {
      const meta = await onUploadPhoto(question.id, file);
      onChange([...photos, meta]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Error subiendo foto');
    } finally {
      setUploading(false);
    }
  }

  function removePhoto(idx: number): void {
    onChange(photos.filter((_, i) => i !== idx));
  }

  return (
    <section aria-label={`Evidencias de la pregunta ${question.id}`} className="space-y-3">
      {/* Botón disparador re-skin + placeholder de upload — lógica intacta */}
      {!readOnly && (
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-cumple-gray-700">
            Evidencias ({photos.length})
          </span>
          {/* Button E1 secondary — area táctil min-h-[44px] por CVA base */}
          <Button
            variant="secondary"
            size="sm"
            disabled={uploading || !onUploadPhoto}
            loading={uploading}
            loadingText="Subiendo…"
            onClick={() => fileRef.current?.click()}
            aria-label="Subir evidencia fotográfica"
          >
            <Camera className="h-4 w-4" aria-hidden="true" />
            {uploading ? 'Subiendo…' : 'Subir evidencia'}
          </Button>
        </div>
      )}

      {/* Grilla responsive tablet-first — UX spec H3.3 */}
      {photos.length > 0 && (
        <ul
          role="list"
          aria-label="Evidencias subidas"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 xl:grid-cols-4 gap-3"
        >
          {photos.map((p, idx) => (
            <li
              key={p.id}
              className="group relative min-h-[80px] aspect-square overflow-hidden rounded-lg border border-slate-200 bg-cumple-gray-100"
            >
              {p.file_url.startsWith('idb://') ? (
                /* Placeholder offline-first — señal visual de sync pendiente */
                <div
                  role="status"
                  aria-label="Foto local pendiente de sincronización"
                  className="flex h-full items-center justify-center text-center px-2"
                >
                  <span className="text-[10px] font-medium text-slate-500 leading-snug">
                    Foto local
                    <br />
                    pendiente sync
                  </span>
                </div>
              ) : (
                <img
                  src={p.file_url}
                  alt={`Evidencia ${idx + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removePhoto(idx)}
                  className="absolute top-1 right-1 rounded-full bg-white/90 p-1 text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm min-h-[32px] min-w-[32px] flex items-center justify-center"
                  aria-label={`Eliminar foto ${idx + 1}`}
                >
                  <svg viewBox="0 0 16 16" className="h-3 w-3" fill="currentColor">
                    <path
                      d="M4 4l8 8M12 4l-8 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
              {/* Overlay con número: texto alt + contraste asegurado (bg-black/60 sobre img) */}
              <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                {idx + 1}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* SH-2: E1 EmptyState size="sm" reemplaza texto ad-hoc inline */}
      {!readOnly && photos.length === 0 && !uploading && (
        <EmptyState
          size="sm"
          variant="empty"
          title="Sin evidencias"
          description="Pulsa 'Subir evidencia' para añadir una foto a esta pregunta."
        />
      )}

      {/*
        TODO(deuda-tecnica): DEUDA-E3-002 — asociación evidencia→hallazgo pospuesta;
        vive en FindingDetail (fuera de alcance core E3). El Select de hallazgo para
        asociar la evidencia post-upload no fue implementado aquí (contexto A, encuesta).
        El contexto B (FindingDetail) es el canal principal confirmado por el Director.
      */}

      {/* SH-2: E1 LoadingSkeleton variant="block" reemplaza div animate-pulse ad-hoc */}
      {uploading && (
        <LoadingSkeleton
          variant="block"
          rows={1}
          label="Subiendo evidencia…"
          className="h-20 w-20"
        />
      )}

      {/*
        INPUT INTOCABLE — REGLA PRINCIPAL E3:
        accept="image/*" y capture="environment" NO se modifican.
        e.target.value = '' mantiene el reset para permitir subir la misma foto.
        handleFile y onUploadPhoto se llaman byte-a-byte como antes.
      */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        aria-label="Subir evidencia fotográfica"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
      />
      {err && (
        <p role="alert" className="text-xs text-rose-600">
          {err}
        </p>
      )}
    </section>
  );
}

function SignatureInput({ question, value, onChange, readOnly }: FieldProps): JSX.Element {
  // MVP signature: text-based "signed by name" + date. Sprint posterior:
  // canvas pad. Mantenemos la API estable.
  const v = typeof value === 'string' ? value : '';
  return (
    <div className="space-y-2">
      <input
        type="text"
        placeholder="Nombre y apellido del firmante"
        value={v}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        className={inputClass}
        aria-label={question.label}
      />
      <p className="text-xs text-slate-400">
        Al cerrar el assessment se sella con timestamp y hash. La firma digital con canvas llega en
        una iteración posterior.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// QuestionEvidence (S15-C)
//
// Panel colapsable bajo cada QuestionCard. Permite al auditor adjuntar
// fotos suplementarias y dejar una nota contextual. La nota se persiste
// en survey_instance.auditor_notes[qid] y se snapshot en el finding
// si se detecta no-conformidad.
// ─────────────────────────────────────────────────────────────────────
interface QuestionEvidenceProps {
  questionId: string;
  note: string;
  onNoteChange: (note: string) => void;
  photos: QuestionPhotoSummary[];
  onUploadPhoto?: (file: File) => Promise<UploadedPhotoMeta>;
  /** S20-B · Si presente, debajo de cada thumbnail aparece editor de chips IMS. */
  onPatchPhotoStandards?: PatchPhotoStandardsHandler;
  readOnly: boolean;
}

function QuestionEvidence({
  questionId,
  note,
  onNoteChange,
  photos,
  onUploadPhoto,
  onPatchPhotoStandards,
  readOnly,
}: QuestionEvidenceProps): JSX.Element {
  const [open, setOpen] = useState(note.length > 0 || photos.length > 0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function handleFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file || !onUploadPhoto) return;
    setUploading(true);
    setUploadError(null);
    try {
      await onUploadPhoto(file);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error subiendo foto');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  const hasContent = note.length > 0 || photos.length > 0;

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-xs font-semibold text-slate-600 hover:text-brand-700"
        aria-expanded={open}
        aria-controls={`evidence-panel-${questionId}`}
      >
        <span className="flex items-center gap-2">
          <span className="text-[10px]" aria-hidden>
            {open ? '▾' : '▸'}
          </span>
          EVIDENCIA Y NOTA DEL AUDITOR
          {hasContent && (
            <span className="ml-1 text-[10px] font-bold text-brand-700">
              · {photos.length > 0 ? `${photos.length} foto${photos.length === 1 ? '' : 's'}` : ''}
              {photos.length > 0 && note.length > 0 ? ' · ' : ''}
              {note.length > 0 ? 'nota' : ''}
            </span>
          )}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          {open ? 'Cerrar' : 'Abrir'}
        </span>
      </button>

      {open && (
        <div id={`evidence-panel-${questionId}`} className="mt-3 space-y-3">
          {photos.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                Fotos subidas para esta pregunta
              </p>
              <ul className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {photos.map((p) => (
                  <li key={p.id} className="space-y-1">
                    <a href={p.file_url} target="_blank" rel="noreferrer" className="block">
                      <img
                        src={p.file_url}
                        alt="Evidencia"
                        className="w-full aspect-square object-cover rounded-md border border-slate-200 hover:opacity-90"
                        loading="lazy"
                      />
                    </a>
                    <PhotoApplicableStandards
                      photoId={p.id}
                      standards={p.applicable_standards ?? []}
                      {...(onPatchPhotoStandards && !readOnly
                        ? { onPatch: onPatchPhotoStandards }
                        : {})}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {onUploadPhoto && !readOnly && (
            <div className="space-y-1">
              {/*
                INPUT INTOCABLE: accept y onChange (handleFile) sin modificar.
                Solo se re-skinea el disparador visual (Button E1 secondary).
              */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                onChange={(e) => {
                  void handleFile(e);
                }}
                disabled={uploading}
                aria-label="Adjuntar foto de evidencia del hallazgo"
                className="sr-only"
                id={`evidence-upload-${questionId}`}
              />
              {/* Button E1 secondary disparando el input sr-only — min-h-[44px] por CVA */}
              <Button
                variant="secondary"
                size="sm"
                disabled={uploading}
                loading={uploading}
                loadingText="Subiendo…"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Adjuntar foto de evidencia del hallazgo"
              >
                <Camera className="h-4 w-4" aria-hidden="true" />
                {uploading ? 'Subiendo…' : '+ Adjuntar foto de evidencia'}
              </Button>
              {/* SH-2: E1 LoadingSkeleton variant="block" reemplaza div animate-pulse ad-hoc */}
              {uploading && (
                <LoadingSkeleton
                  variant="block"
                  rows={1}
                  label="Subiendo evidencia…"
                  className="h-16 w-16"
                />
              )}
              {uploadError && (
                <p role="alert" className="mt-1 text-[11px] text-rose-600">
                  {uploadError}
                </p>
              )}
            </div>
          )}

          <div>
            <label
              htmlFor={`note-${questionId}`}
              className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1"
            >
              Nota del auditor (opcional)
            </label>
            <textarea
              id={`note-${questionId}`}
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              disabled={readOnly}
              rows={2}
              maxLength={2000}
              placeholder="Observaciones contextuales — ej. control evidenciado por declaración del responsable, falta política firmada, etc."
              className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-100"
            />
            {note.length > 1800 && (
              <p className="mt-0.5 text-[10px] text-slate-400">{note.length} / 2000 caracteres</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PhotoApplicableStandards (S20-B)
//
// Chips IMS por foto. Si la foto sirve como evidencia cruzada de
// multiples normas (ej. la misma evidencia documental cubre Cl. 5.2 de
// ISO 9001 + Cl. 5.2 de ISO 14001 dentro de un IMS), las normas se
// listan aqui. Si onPatch llega, se permite editar inline; si no,
// modo read-only.
// ─────────────────────────────────────────────────────────────────────
function PhotoApplicableStandards({
  photoId,
  standards,
  onPatch,
}: {
  photoId: string;
  standards: string[];
  onPatch?: PatchPhotoStandardsHandler;
}): JSX.Element | null {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localStandards, setLocalStandards] = useState<string[]>(standards);

  // Mantener sync con prop cuando cambia (re-fetch externo).
  // No usamos useEffect para evitar carrera con saving local.
  if (!editing && localStandards !== standards && !saving) {
    setLocalStandards(standards);
  }

  const readOnly = !onPatch;
  if (readOnly && localStandards.length === 0) return null;

  async function commit(next: string[]): Promise<void> {
    if (!onPatch) return;
    setSaving(true);
    setError(null);
    try {
      await onPatch(photoId, next);
      setLocalStandards(next);
      setEditing(false);
      setDraft('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error etiquetando foto');
    } finally {
      setSaving(false);
    }
  }

  function addChip(): void {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (localStandards.includes(trimmed)) {
      setDraft('');
      return;
    }
    if (localStandards.length >= 20) {
      setError('Maximo 20 etiquetas');
      return;
    }
    void commit([...localStandards, trimmed]);
  }

  function removeChip(s: string): void {
    void commit(localStandards.filter((x) => x !== s));
  }

  return (
    <div className="text-[10px] leading-tight">
      <div className="flex flex-wrap gap-1">
        {localStandards.map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 rounded bg-indigo-100 px-1.5 py-0.5 font-mono text-indigo-800"
          >
            {s}
            {!readOnly && (
              <button
                type="button"
                onClick={() => removeChip(s)}
                disabled={saving}
                aria-label={`Quitar ${s}`}
                className="text-indigo-500 hover:text-indigo-900 disabled:opacity-40"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {!readOnly && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded border border-dashed border-indigo-300 px-1.5 py-0.5 font-medium text-indigo-600 hover:bg-indigo-50"
          >
            + etiqueta
          </button>
        )}
      </div>
      {!readOnly && editing && (
        <div className="mt-1 flex gap-1">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addChip();
              } else if (e.key === 'Escape') {
                setEditing(false);
                setDraft('');
                setError(null);
              }
            }}
            disabled={saving}
            autoFocus
            placeholder="Ej. ISO9001:2015"
            maxLength={64}
            className="flex-1 min-w-0 rounded border border-indigo-300 px-1.5 py-0.5 text-[10px] font-mono focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 disabled:bg-slate-100"
          />
          <button
            type="button"
            onClick={addChip}
            disabled={saving || !draft.trim()}
            className="rounded bg-indigo-600 px-1.5 py-0.5 font-semibold text-white hover:bg-indigo-700 disabled:bg-indigo-300"
          >
            +
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-0.5 text-rose-600">
          {error}
        </p>
      )}
    </div>
  );
}
